<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'tableCom',
})
</script>
<template>
	<el-table
		ref="tableRef"
		v-loading="props.loading"
		style="width: 100%"
		:data="props.data"
		:max-height="props.maxHeight"
		border
		stripe
		:show-summary="showSummary"
		:sum-text="t('componentsModule.common.tableCom.total')"
		:row-key="rowKey"
		:default-expand-all="defaultExpandAll"
		:cell-style="setCellStyle"
		:summary-method="getSummaries"
		@row-click="handleRowClick"
		@row-dblclick="handleRowDblclick"
		@selection-change="selectionChangeHandle"
		@sort-change="handleSortChange"
		@expand-change="handleExpandChange"
	>
		<template v-for="item in props.config" :key="(item.type || '') + (item.prop || '')">
			<el-table-column v-if="item.type == 'selection'" type="selection" header-align="left" align="left" width="40"></el-table-column>
			<el-table-column v-if="item.type == 'expand'" type="expand" header-align="left" align="left" width="40">
				<template #default="props">
					<slot name="expand" :row="props.row"></slot>
				</template>
			</el-table-column>
			<el-table-column
				v-if="item.type == 'slot'"
				:prop="item.prop"
				:label="item.label"
				:align="item.align || 'left'"
				:header-align="item.align || 'left'"
				:sortable="item.sortable"
				show-overflow-tooltip
				:width="item.width"
				:fixed="item.fixed"
			>
				<template #default="scope">
					<slot :name="item.prop" :row="scope.row" :index="scope.$index"></slot>
				</template>
			</el-table-column>
			<ma-dict-column
				v-if="item.type == 'dict'"
				:prop="item.prop"
				:label="item.label"
				:dict-type="item.dict"
				show-overflow-tooltip
				:width="item.width"
			></ma-dict-column>
			<ma-dict-column-list
				v-if="item.type == 'dict_list'"
				:prop="item.prop"
				:label="item.label"
				:dict-type="item.dict"
				show-overflow-tooltip
				:width="item.width"
			/>
			<el-table-column
				v-if="!item.type && item.prop"
				:prop="item.prop"
				:label="item.label"
				:align="item.align || 'left'"
				:header-align="item.align || 'left'"
				:sortable="item.sortable"
				show-overflow-tooltip
				:width="item.width"
			>
				<template #default="scope">
					<template v-if="isStatusField(scope.row[item.prop])">
						<el-tag :type="isPositiveStatus(scope.row[item.prop])" size="small">
							{{ scope.row[item.prop] }}
						</el-tag>
					</template>
					<template v-else>
						<span :style="item.customStyle ? item.customStyle(scope.row) : {}">
							{{ scope.row[item.prop] }}
						</span>
					</template>
				</template>
			</el-table-column>
			<el-table-column
				v-if="item.type == 'select'"
				:prop="item.prop"
				:label="item.label"
				:align="item.align || 'left'"
				:header-align="item.align || 'left'"
				:sortable="item.sortable"
				show-overflow-tooltip
				:width="item.width"
			>
				<template #default="scope">
					<el-tag
						v-if="getSelectTagType(scope.row[item.prop], item.options)"
						:type="getSelectTagType(scope.row[item.prop], item.options)"
						size="small"
					>
						{{ getSelectLabel(scope.row[item.prop], item.options) }}
					</el-tag>
					<span v-else>{{ getSelectLabel(scope.row[item.prop], item.options) }}</span>
				</template>
			</el-table-column>
			<el-table-column v-if="item.type == 'file'" :prop="item.prop" :label="item.label" align="left" :width="item.width">
				<template #default="scope">
					<span v-for="i in scope.row[item.prop]" :key="i">
						<el-popover placement="left" width="200" trigger="hover" :content="i.name">
							<template #reference>
								<img :src="getFileTypeSvg(i.url)" :alt="i.url" class="file-icon" @click="handleFileView(i.url)" />
							</template>
						</el-popover>
					</span>
				</template>
			</el-table-column>
			<el-table-column v-if="item.type == 'image'" :prop="item.prop" :label="item.label" align="left" show-overflow-tooltip :width="item.width">
				<template #default="scope">
					<el-image :style="`height: ${setCellStyle().height}; `" :src="constant.apiUrl + scope.row[item.prop]" />
				</template>
			</el-table-column>
			<el-table-column v-if="item.type == 'icon'" :prop="item.prop" :label="item.label" align="left" show-overflow-tooltip :width="item.width">
				<template #default="scope">
					<ma-icon :icon="scope.row[item.prop]" class-name="svg-size" />
				</template>
			</el-table-column>
			<el-table-column v-if="item.type == 'filesize'" :prop="item.prop" :label="item.label" align="left" :width="item.width">
				<template #default="scope">
					{{ convertSizeFormat(scope.row[item.prop]) }}
				</template>
			</el-table-column>
			<el-table-column v-if="item.type == 'assembly'" :prop="item.prop" :label="item.label" align="left" show-overflow-tooltip :width="item.width">
				<template #default="scope">
					<span v-for="i in item.prop.split(',')" :key="i"> {{ scope.row[i] }} </span>
				</template>
			</el-table-column>
			<el-table-column v-if="item.type == 'fileType'" :prop="item.prop" :label="item.label" align="left" show-overflow-tooltip :width="item.width">
				<template #default="scope">
					<div style="">
						<ma-icon :icon="`${getFileTypeIcon(scope.row[item.prop])}`" :size="'4rem'" class-name="svg-size" />
					</div>
				</template>
			</el-table-column>
			<el-table-column v-if="item.type == 'link'" :prop="item.prop" :label="item.label" align="left" show-overflow-tooltip :width="item.width">
				<template #default="scope">
					<div v-if="scope.row[item.prop]" style="display: flex; align-items: center">
						<img :src="getFileTypeSvg(scope.row[item.url])" class="file-icon" />
						<el-link type="primary" @click="handleFileView(scope.row[item.url])">
							{{ scope.row[item.prop] }}
						</el-link>
					</div>
				</template>
			</el-table-column>
			<el-table-column v-if="item.type == 'timestamp'" :prop="item.prop" :label="item.label" align="left" show-overflow-tooltip :width="item.width">
				<template #default="scope">
					{{ dayjs(Number(scope.row[item.prop]) * 1000).format('YYYY/MM/DD HH:mm:ss') }}
				</template>
			</el-table-column>
			<el-table-column
				v-if="item.type == 'datetime'"
				:prop="item.prop"
				:label="item.label"
				align="left"
				show-overflow-tooltip
				:width="item.width || 130"
			>
				<template #default="scope">
					{{ dayjs(scope.row[item.prop]).format(item.format || 'YYYY/MM/DD HH:mm:ss') }}
				</template>
			</el-table-column>
			<el-table-column v-if="item.type == 'obj'" :prop="item.prop" :label="item.label" align="left" :width="item.width || 130">
				<template #default="scope">
					<div class="obj-cell">
						<div
							v-for="i in scope.row[item.prop]"
							:key="`${i.label}-${i.sort ?? i.value}`"
							class="obj-cell-item"
							:style="`width: ${100 / (item?.split ?? 1)}%`"
						>
							<div class="obj-cell-label">{{ i.label }}：</div>
							<div class="obj-cell-value">{{ formatObjValue(i.value) }}</div>
						</div>
					</div>
				</template>
			</el-table-column>
			<el-table-column v-if="item.type == 'switch'" :prop="item.prop" :label="item.label" align="center" :width="item.width || 100">
				<template #default="scope">
					<el-switch
						:model-value="scope.row[item.prop]"
						:active-value="item.options?.active?.value || 1"
						:inactive-value="item.options?.inactive?.value || 0"
						@change="val => handleSwitchChange(scope.row, item, val)"
					/>
				</template>
			</el-table-column>
			<el-table-column
				v-if="item.type == 'blue-text'"
				:prop="item.prop"
				:label="item.label"
				:align="item.align || 'left'"
				:header-align="item.align || 'left'"
				:sortable="item.sortable"
				show-overflow-tooltip
				:width="item.width"
			>
				<template #default="scope">
					<span class="blue-text-field" @click="handleBlueTextClick(scope.row, item)">
						{{ scope.row[item.prop] }}
					</span>
				</template>
			</el-table-column>
			<el-table-column
				v-if="item.type == 'action'"
				:label="t('componentsModule.common.tableCom.action')"
				fixed="right"
				header-align="left"
				align="left"
				:width="item.width || 200"
			>
				<template #default="scope">
					<slot name="action" :row="scope.row"></slot>
					<template v-if="item.buttons && item.buttons.length > 0">
						<template v-for="i in item.buttons" :key="i">
							<el-dropdown v-if="i.dropdown" trigger="click" @command="command => handleDropdownCommand(command, scope.row)">
								<el-button v-show="(i.if !== undefined ? i.if(scope.row) : true) && hasAuth(i.auth)" :icon="i.icon" :type="i.type" link>
									{{ i.text }}
									<el-icon class="el-icon--right"><arrow-down /></el-icon>
								</el-button>
								<template #dropdown>
									<el-dropdown-menu>
										<el-dropdown-item
											v-for="subItem in i.items"
											:key="subItem.text"
											:command="{ handler: subItem.click, row: scope.row }"
											:icon="subItem.icon"
										>
											{{ subItem.text }}
										</el-dropdown-item>
									</el-dropdown-menu>
								</template>
							</el-dropdown>
							<el-button
								v-else
								v-show="(i.if !== undefined ? i.if(scope.row) : true) && hasAuth(i.auth)"
								:icon="i.icon"
								:type="i.type"
								link
								@click.stop="i.click(scope.row)"
								>{{ i.text }}</el-button
							>
						</template>
					</template>
				</template>
			</el-table-column>
		</template>
	</el-table>
	<div style="display: flex; align-items: center; justify-content: flex-end">
		<el-pagination
			v-if="isPage"
			small
			style="margin-top: 10px"
			:current-page="props.page"
			:page-sizes="props.pageSizes"
			:page-size="props.limit"
			:total="props.total"
			layout="total, sizes, prev, pager, next, jumper"
			@size-change="sizeChangeHandle"
			@current-change="currentChangeHandle"
		>
		</el-pagination>
	</div>

	<el-dialog v-model="fileListShow" :title="t('componentsModule.common.attachmentList.title')" :close-on-click-modal="false" width="50vw">
		<div style="overflow: hidden">
			<attachment-list :title="''" :file-list="currentFileList" />
		</div>
	</el-dialog>
	<!-- 在线预览组件 -->
	<OnlinePreview v-model="fileShow" :file-url="currentFileUrl" @closed="closeView" />
</template>
<script setup lang="ts" name="tableCom">
import { defineProps, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { getFileTypeIcon, handlePreview, getFileUrl } from '@/utils/tool'
import { convertSizeFormat } from '@/utils/tool'
import VueOfficePptx from '@vue-office/pptx'
import VueOfficePdf from '@vue-office/pdf'
import VueOfficeExcel from '@vue-office/excel'
import '@vue-office/excel/lib/index.css'
import '@vue-office/docx/lib/index.css'
import { getFileTypeSvg } from '@/utils/fileTypeIcon'
import constant from '@/utils/constant'
import dayjs from 'dayjs'
import { useUserStore } from '@/store/modules/user'
import { ArrowDown } from '@element-plus/icons-vue'
import * as attachmentList from './attachmentList.vue'
// import * as OnlinePreview from './OnlinePreview.vue'

const { t } = useI18n()
onMounted(() => {})
const props = defineProps({
	data: {
		type: Object as () => Record<string, any>,
		default: null
	},
	loading: {
		type: Boolean,
		default: false
	},
	maxHeight: {
		type: Number,
		default: null
	},
	page: {
		type: Number,
		default: 1
	},
	pageSizes: {
		type: Object as () => Record<string, any>,
		default() {
			return 20
		}
	},
	limit: {
		type: Number,
		default: 10
	},
	total: {
		type: Number,
		default: 0
	},
	config: {
		type: Array as () => Record<string, any>,
		default() {
			return []
		}
	},
	btns: {
		type: Array as () => Record<string, any>,
		default() {
			return []
		}
	},
	isPage: {
		type: Boolean,
		default: true
	},
	defaultExpandAll: {
		type: Boolean,
		default: false
	},
	rowKey: {
		type: String,
		default: ''
	},
	showSummary: {
		type: Boolean,
		default: false
	}
})
const fileShow = ref(false)
const currentFileUrl = ref('')
const fileListShow = ref(false)
const currentFileList = ref<any>([])
const userStore = useUserStore()

// 权限检查函数
const hasAuth = (auth: string) => {
	if (!auth) {
		return true
	}
	return userStore.authorityList.some((v: string) => v === auth)
}
const emit = defineEmits([
	'selectionChangeHandle',
	'sortChangeHandle',
	'sizeChangeHandle',
	'deleteBatchHandle',
	'addOrUpdateHandle',
	'currentChangeHandle',
	'handleView',
	'handleExpandChange',
	'rowDblclick',
	'blueTextClick',
	'switchChange'
])

const isStatusField = (value: any) => {
	if (value === undefined || value === null) {
		return false
	}
	const statusValues = ['在线', '离线', '正常', '停机', '异常', '激活', '未激活']
	return typeof value === 'string' && statusValues.includes(value)
}
const isPositiveStatus = (value: any) => {
	let type = 'success'
	if (value === '离线') {
		type = 'info'
	} else if (value === '异常') {
		type = 'error'
	} else if (value === '未激活') {
		type = 'warning'
	} else if (value === '停机') {
		type = 'warning'
	}
	return type
}

const setCellStyle = ({ column }: any = {}) => {
	const columnConfig = props.config.find((item: any) => item.prop === column?.property)
	if (columnConfig?.type === 'obj') {
		return {
			paddingTop: '8px',
			paddingBottom: '8px',
			lineHeight: '20px'
		}
	}
	return { lineHeight: '30px', height: '30px' }
}
const getSummaries = ({ columns, data }: any) => {
	const sums: any[] = []
	console.log('columns', columns)
	console.log('data', data)
	columns.forEach((column: any) => {
		const prop = column.property
		const values = data.map((row: any) => row[prop])

		if (typeof values[0] === 'number') {
			const sum = values.reduce((acc: any, cur: any) => acc + cur, 0)
			sums.push(sum)
		} else {
			sums.push('')
		}
	})
	return sums
}
const sizeChangeHandle = (e: number) => {
	emit('sizeChangeHandle', e)
}
const handleSortChange = (e: any) => {
	emit('sortChangeHandle', e)
}
const handleFileView = (e: any) => {
	console.log('handleFileView', e)
	// 获取文件扩展名
	const fileExtension = e.split('.').pop()?.toLowerCase()

	// 判断是否为压缩包文件
	const compressedFileTypes = ['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz']

	if (compressedFileTypes.includes(fileExtension)) {
		// 如果是压缩包文件，直接下载
		window.open(constant.apiUrl + e, '_blank')
	} else {
		// 其他文件类型进行预览
		currentFileUrl.value = e
		fileShow.value = true
	}
}
const befDict = (val: any) => {}
const currentChangeHandle = (e: number) => {
	emit('currentChangeHandle', e)
}
const handleView = (e: any) => {
	emit('handleView', e)
}
const addOrUpdateHandle = (e: any) => {
	emit('addOrUpdateHandle', e)
}
const deleteBatchHandle = (e: any) => {
	emit('deleteBatchHandle', e)
}
const selectionChangeHandle = (e: any) => {
	emit('selectionChangeHandle', e)
}
const tableRef = ref()
const handleRowClick = (row: any) => {
	if (props.config.some((item: any) => item.type === 'expand')) {
		tableRef.value.toggleRowExpansion(row)
	}
}
const handleRowDblclick = (row: any) => {
	emit('rowDblclick', row)
}
const handleExpandChange = (row: any, expandedRows: any[]) => {
	emit('handleExpandChange', row, expandedRows)
}
const isImage = (url: any) => {
	return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(url)
}
const handleFileListShow = (row: any) => {
	fileListShow.value = true
	currentFileList.value = row
}

const formatObjValue = (value: any) => {
	if (value === null || value === undefined || value === '') {
		return value
	}
	const text = String(value).trim()
	if (!/^-?\d+(\.\d+)?$/.test(text)) {
		return value
	}
	const [, decimal = ''] = text.split('.')
	if (decimal.length <= 4) {
		return text
	}
	return Number(text)
		.toFixed(4)
		.replace(/\.?0+$/, '')
}

const getSelectLabel = (value: any, options: any[]) => {
	if (!options || !Array.isArray(options)) {
		return value
	}
	const option = options.find((item: any) => item.value === value)
	return option ? option.label : value
}
const getSelectTagType = (value: any, options: any[]): string | null => {
	if (!options || !Array.isArray(options)) {
		return null
	}
	const option = options.find((item: any) => item.value === value)
	return option?.tagType || null
}

const closeView = () => {
	fileShow.value = false
	currentFileUrl.value = ''
}

const handleBlueTextClick = (row: any, item: any) => {
	emit('blueTextClick', row, item)
}

const handleSwitchChange = (row: any, item: any, value: any) => {
	emit('switchChange', row, item, value)
}

// 处理下拉菜单命令
const handleDropdownCommand = (command: any, row: any) => {
	command.handler(command.row)
}

defineExpose({ tableRef })
</script>
<style lang="scss" scoped>
.file-icon {
	width: 30px;
	height: 30px;
	flex-shrink: 0;
	cursor: pointer;
}

.blue-text-field {
	color: #409eff;
	cursor: pointer;
	transition: color 0.3s ease;

	&:hover {
		color: #66b1ff;
		text-decoration: underline;
	}
}

.obj-cell {
	display: flex;
	flex-wrap: wrap;
	align-items: flex-start;
	row-gap: 6px;
}

.obj-cell-item {
	display: flex;
	align-items: flex-start;
	min-height: 24px;
	padding-right: 8px;
	box-sizing: border-box;
	line-height: 20px;
}

.obj-cell-label {
	flex-shrink: 0;
}

.obj-cell-value {
	flex: 1;
	min-width: 0;
	word-break: break-all;
	white-space: normal;
}
</style>
